import { useCallback, useEffect, useState } from 'react';
import { callApi } from 'apiClient';
import { Permission } from 'shared/permissions';
import { useDispatch } from 'react-redux';
import { useRouteMatch } from 'react-router';
import { useTranslation } from 'react-i18next';
import { loginSuccess } from 'actions/sessionActions';
import { push as navigateTo } from 'connected-react-router';
import { VerifyResetPasswordForm } from '../verifyResetPasswordForm/VerifyResetPasswordForm';
import { useSession } from 'hooks';
import { toError } from 'shared/util/toError';
import { VerifyResetPasswordExpired } from '../verifyResetPasswordExpired/VerifyResetPasswordExpired';
import { featuresLoaded } from 'actions/featuresActions';

export const VerifyResetPasswordPage = () => {
    const { t } = useTranslation();
    const { hasPermission } = useSession();
    const match = useRouteMatch<{ resetToken: string }>('/reset-password/:resetToken');
    const resetToken = match && match.params.resetToken;

    const dispatch = useDispatch();

    const [ busy, setBusy ] = useState(false);
    const [ errorMessage, setErrorMessage ] = useState('');
    const [ tokenIsValid, setTokenIsValid ] = useState(false);

    const handleReset = useCallback(async (password: string) => {
        setBusy(true);
        try {
            const result = await callApi('POST', '/api/password-reset/verify', { resetToken, password });
            const { session, features } = result;
            const { user, permissions } = session;
            dispatch(loginSuccess({ user, permissions }));
            dispatch(featuresLoaded(features));
    
            const isAdmin = hasPermission(null, Permission.AllBuildings) || hasPermission(null, Permission.AllOrganizations);
            dispatch(navigateTo(isAdmin ? '/admin' : '/'));
        }
        catch (e) {
            const err = toError(e);
            const errorMessage = t([ `VERIFY_RESET_PASSWORD.ERROR.${err.message}`, 'SERVER_ERROR' ]);
            setErrorMessage(errorMessage);
        }
        finally {
            setBusy(false);
        }
    }, [ resetToken, setBusy, setErrorMessage, dispatch, t ]);

    useEffect(() => {
        void (async () => {
            setBusy(true);
            setErrorMessage('');
            try {
                const tokenExists = await callApi('GET', `/api/password-reset/check/${resetToken}`);
                setTokenIsValid(tokenExists);
                if (!tokenExists) {
                    setErrorMessage(t('VERIFY_RESET_PASSWORD.ERROR.NOT_FOUND'));
                }
            }
            catch (e) {
                const err = toError(e);
                setErrorMessage(t([ `VERIFY_RESET_PASSWORD.ERROR.${err.message}`, 'SERVER_ERROR' ]));
            }
            finally {
                setBusy(false);
            }
        })();
    }, [ setErrorMessage, setTokenIsValid, setBusy ]);

    return (
        <div id="verify-reset-password-page">
            { tokenIsValid || busy ?
                <VerifyResetPasswordForm
                    busy={ busy }
                    errorMessage={ errorMessage }
                    onSubmit={ handleReset }
                /> :
                <VerifyResetPasswordExpired
                    errorMessage={ errorMessage }
                />
            }
        </div>
    );
};
